import {
  normalizeAnkiPing,
  normalizeAnkiStatus,
  normalizeAnkiSync,
  normalizeSettings,
  normalizeUser,
  normalizeVocab,
  normalizeVocabList,
} from './contract';
import type {
  AnkiPingResponse,
  AnkiStatus,
  AnkiSyncResult,
  ExportFormat,
  HealthResponse,
  Settings,
  SettingsUpdate,
  TokenPair,
  UserOut,
  Vocab,
  VocabCreate,
  VocabImportRequest,
  VocabImportResult,
  VocabList,
  VocabSort,
  VocabUpdate,
} from './types';

const REFRESH_STORAGE_KEY = 'olr.refresh_token';
const API_PREFIX = '/api/v1';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  /** Parsed response body when it was JSON — some endpoints answer errors with a
   * documented payload instead of the error envelope (`/anki/ping` 503). */
  readonly payload: unknown;

  constructor(message: string, options: { status: number; code?: string | null; payload?: unknown }) {
    super(message);
    this.name = 'ApiError';
    this.status = options.status;
    this.code = options.code ?? null;
    this.payload = options.payload ?? null;
  }
}

export interface ApiClientOptions {
  /** Same-origin by default; `VITE_OLR_API_BASE` allows a split deployment. */
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
}

export interface VocabQuery {
  limit?: number;
  offset?: number;
  q?: string;
  sourceLanguage?: string;
  targetLanguage?: string;
  sort?: VocabSort;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Skip the Authorization header and the 401-refresh dance. */
  anonymous?: boolean;
  signal?: AbortSignal;
}

function defaultBaseUrl(): string {
  const configured = (import.meta.env?.VITE_OLR_API_BASE as string | undefined) ?? '';
  return configured.replace(/\/$/, '');
}

/**
 * Thin typed client for the OLR API.
 *
 * Session model: the **access token lives in memory only** (a page reload loses
 * it, then a silent refresh restores it); the **refresh token is in
 * localStorage**, which is a documented trade-off for a self-hosted single-user
 * deployment — see `README.md`.
 */
export class ApiClient {
  private accessToken: string | null = null;
  private refreshInFlight: Promise<boolean> | null = null;
  private readonly authLostListeners = new Set<() => void>();
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private readonly storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
  private user: UserOut | null = null;

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = (options.baseUrl ?? defaultBaseUrl()).replace(/\/$/, '');
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.storage =
      options.storage ?? (typeof localStorage === 'undefined' ? null : localStorage);
  }

  get loadedUser(): UserOut | null {
    return this.user;
  }

  get refreshToken(): string | null {
    if (this.storage === null) return null;
    try {
      return this.storage.getItem(REFRESH_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  /** True when a page reload could restore the session. */
  get canRestoreSession(): boolean {
    return this.refreshToken !== null;
  }

  onAuthLost(listener: () => void): () => void {
    this.authLostListeners.add(listener);
    return () => this.authLostListeners.delete(listener);
  }

  private setSession(pair: TokenPair): void {
    this.accessToken = pair.access_token;
    this.user = pair.user;
    if (this.storage !== null) {
      try {
        this.storage.setItem(REFRESH_STORAGE_KEY, pair.refresh_token);
      } catch {
        // Storage may be full or blocked; the in-memory session still works.
      }
    }
  }

  clearSession(): void {
    this.accessToken = null;
    this.user = null;
    if (this.storage !== null) {
      try {
        this.storage.removeItem(REFRESH_STORAGE_KEY);
      } catch {
        // ignore
      }
    }
  }

  private async send(path: string, options: RequestOptions): Promise<Response> {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (options.body !== undefined) headers['content-type'] = 'application/json';
    if (!options.anonymous && this.accessToken !== null) {
      headers.authorization = `Bearer ${this.accessToken}`;
    }
    return this.fetchImpl(`${this.baseUrl}${API_PREFIX}${path}`, {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  }

  private async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    let response = await this.send(path, options);

    if (response.status === 401 && options.anonymous !== true && this.refreshToken !== null) {
      const refreshed = await this.refreshSession();
      if (refreshed) response = await this.send(path, options);
    }

    if (response.status === 204) return undefined as T;
    if (!response.ok) throw await toApiError(response);
    const text = await response.text();
    if (text.length === 0) return undefined as T;
    return JSON.parse(text) as T;
  }

  private async refreshSession(signal?: AbortSignal): Promise<boolean> {
    if (this.refreshInFlight !== null) return this.refreshInFlight;

    const token = this.refreshToken;
    if (token === null) return false;

    this.refreshInFlight = (async () => {
      try {
        const response = await this.send('/auth/refresh', {
          method: 'POST',
          body: { refresh_token: token },
          anonymous: true,
          signal,
        });
        if (!response.ok) {
          this.clearSession();
          this.notifyAuthLost();
          return false;
        }
        this.setSession((await response.json()) as TokenPair);
        return true;
      } catch (error) {
        // A caller-side abort must reach the caller (boot timeout), otherwise it
        // would look like "no session" and strand the user on the login screen.
        if (error instanceof DOMException && error.name === 'AbortError') throw error;
        // Network failure is not proof that the session is dead; keep the token
        // and let the caller surface an error.
        return false;
      } finally {
        this.refreshInFlight = null;
      }
    })();

    return this.refreshInFlight;
  }

  private notifyAuthLost(): void {
    for (const listener of this.authLostListeners) listener();
  }

  // ── auth ──────────────────────────────────────────────────────────────────
  async register(email: string, password: string, displayName?: string): Promise<TokenPair> {
    const pair = await this.request<TokenPair>('/auth/register', {
      method: 'POST',
      anonymous: true,
      body: { email, password, ...(displayName ? { display_name: displayName } : {}) },
    });
    pair.user = normalizeUser(pair.user);
    this.setSession(pair);
    return pair;
  }

  async login(email: string, password: string): Promise<TokenPair> {
    const pair = await this.request<TokenPair>('/auth/login', {
      method: 'POST',
      anonymous: true,
      body: { email, password },
    });
    pair.user = normalizeUser(pair.user);
    this.setSession(pair);
    return pair;
  }

  /** Restore a session after a page reload. Returns false when there is none.
   * `signal` lets the caller bound the boot attempt (see `app.tsx`). */
  async restore(options: { signal?: AbortSignal } = {}): Promise<boolean> {
    if (this.accessToken !== null) return true;
    const refreshed = await this.refreshSession(options.signal);
    if (!refreshed) return false;
    try {
      await this.me();
      return true;
    } catch {
      return false;
    }
  }

  async me(): Promise<UserOut> {
    const user = normalizeUser(await this.request<unknown>('/auth/me'));
    this.user = user;
    return user;
  }

  async logout(): Promise<void> {
    const token = this.refreshToken;
    try {
      await this.request<void>('/auth/logout', {
        method: 'POST',
        body: token === null ? {} : { refresh_token: token },
      });
    } catch {
      // Even if the server call fails, drop the local session.
    }
    this.clearSession();
  }

  // ── health ────────────────────────────────────────────────────────────────
  async health(): Promise<HealthResponse> {
    return this.request<HealthResponse>('/health', { anonymous: true });
  }

  // ── settings ──────────────────────────────────────────────────────────────
  async getSettings(): Promise<Settings> {
    return normalizeSettings(await this.request<unknown>('/settings'));
  }

  async updateSettings(patch: SettingsUpdate): Promise<Settings> {
    return normalizeSettings(await this.request<unknown>('/settings', { method: 'PUT', body: patch }));
  }

  // ── vocabulary ────────────────────────────────────────────────────────────
  async listVocab(query: VocabQuery = {}): Promise<VocabList> {
    const params = new URLSearchParams();
    params.set('limit', String(query.limit ?? 25));
    params.set('offset', String(query.offset ?? 0));
    if (query.q) params.set('q', query.q);
    if (query.sourceLanguage) params.set('source_language', query.sourceLanguage);
    if (query.targetLanguage) params.set('target_language', query.targetLanguage);
    if (query.sort) params.set('sort', query.sort);
    return normalizeVocabList(await this.request<unknown>(`/vocab?${params.toString()}`));
  }

  async createVocab(input: VocabCreate): Promise<Vocab> {
    return normalizeVocab(await this.request<unknown>('/vocab', { method: 'POST', body: input }));
  }

  async updateVocab(id: number, patch: VocabUpdate): Promise<Vocab> {
    return normalizeVocab(await this.request<unknown>(`/vocab/${id}`, { method: 'PATCH', body: patch }));
  }

  async deleteVocab(id: number): Promise<void> {
    await this.request<void>(`/vocab/${id}`, { method: 'DELETE' });
  }

  async importVocab(input: VocabImportRequest): Promise<VocabImportResult> {
    return this.request<VocabImportResult>('/vocab/import', { method: 'POST', body: input });
  }

  /**
   * Fetch an export and hand it to the browser as a download. Streams through a
   * Blob because the endpoint requires the Authorization header (no query token).
   */
  async downloadExport(format: ExportFormat): Promise<string> {
    const response = await this.send(`/vocab/export?format=${format}`, {});
    if (!response.ok) throw await toApiError(response);
    const blob = await response.blob();
    const filename = filenameFrom(response) ?? `olr-vocab-${new Date().toISOString().slice(0, 10)}.${format}`;
    if (typeof document === 'undefined') return filename;
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
    return filename;
  }

  // ── anki ──────────────────────────────────────────────────────────────────
  async ankiPing(url?: string): Promise<AnkiPingResponse> {
    try {
      return normalizeAnkiPing(
        await this.request<unknown>('/anki/ping', { method: 'POST', body: url === undefined ? {} : { url } }),
        url ?? ''
      );
    } catch (error) {
      // A 503 carries the same AnkiPingResponse body with `reachable: false` — a
      // status, not a failure, so surface it as a normal response.
      if (error instanceof ApiError && error.status === 503) {
        return normalizeAnkiPing(error.payload, url ?? '');
      }
      throw error;
    }
  }

  async ankiSync(options: { dryRun: boolean; deck?: string; model?: string; tags?: string[] }): Promise<AnkiSyncResult> {
    return normalizeAnkiSync(
      await this.request<unknown>('/anki/sync', {
        method: 'POST',
        body: {
          dry_run: options.dryRun,
          ...(options.deck === undefined ? {} : { deck: options.deck }),
          ...(options.model === undefined ? {} : { model: options.model }),
          ...(options.tags === undefined ? {} : { tags: options.tags }),
        },
      })
    );
  }

  async ankiStatus(): Promise<AnkiStatus> {
    return normalizeAnkiStatus(await this.request<unknown>('/anki/status'));
  }

  /**
   * Does this backend's `/anki/sync` accept `dry_run`?
   *
   * A preview that silently pushes is worse than no preview, so the UI asks the
   * OpenAPI document first. Any failure (404, disabled docs, offline) answers
   * `false` and the preview button stays disabled.
   */
  async supportsAnkiDryRun(): Promise<boolean> {
    try {
      const response = await this.fetchImpl(`${this.baseUrl}/openapi.json`, {
        headers: { accept: 'application/json' },
      });
      if (!response.ok) return false;
      const spec: unknown = await response.json();
      return hasSchemaProperty(spec, 'AnkiSyncRequest', 'dry_run');
    } catch {
      return false;
    }
  }
}

function filenameFrom(response: Response): string | null {
  const disposition = response.headers.get('content-disposition');
  if (disposition === null) return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  return match?.[1] ?? null;
}

function readErrorPayload(payload: unknown): { message: string; code: string | null } | null {
  if (typeof payload !== 'object' || payload === null) return null;

  if ('error' in payload) {
    const error = payload.error;
    if (typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string') {
      const code = 'code' in error && typeof error.code === 'string' ? error.code : null;
      return { message: error.message, code };
    }
  }

  if ('detail' in payload) {
    const detail = payload.detail;
    if (typeof detail === 'string') return { message: detail, code: null };
    if (Array.isArray(detail)) {
      const parts: string[] = [];
      for (const entry of detail) {
        if (typeof entry === 'string') {
          parts.push(entry);
        } else if (typeof entry === 'object' && entry !== null && 'msg' in entry && typeof entry.msg === 'string') {
          parts.push(entry.msg);
        }
      }
      if (parts.length > 0) return { message: parts.join('; '), code: 'validation' };
    }
  }

  return null;
}

async function toApiError(response: Response): Promise<ApiError> {
  const text = await response.text().catch(() => '');
  let message = response.statusText || `HTTP ${response.status}`;
  let code: string | null = null;

  let payload: unknown = null;
  if (text.length > 0) {
    try {
      payload = JSON.parse(text);
      const parsed = readErrorPayload(payload);
      if (parsed !== null) {
        message = parsed.message;
        code = parsed.code;
      }
    } catch {
      message = text.slice(0, 300);
    }
  }

  return new ApiError(message, { status: response.status, code, payload });
}

/** Application singleton: same origin, real fetch, localStorage. */
export const api = new ApiClient();

function hasSchemaProperty(spec: unknown, schemaName: string, property: string): boolean {
  if (typeof spec !== 'object' || spec === null) return false;
  const { components } = spec as { components?: unknown };
  if (typeof components !== 'object' || components === null) return false;
  const { schemas } = components as { schemas?: unknown };
  if (typeof schemas !== 'object' || schemas === null) return false;
  const schema = (schemas as Record<string, unknown>)[schemaName];
  if (typeof schema !== 'object' || schema === null) return false;
  const { properties } = schema as { properties?: unknown };
  return typeof properties === 'object' && properties !== null && property in properties;
}
